import { cn } from '@/shared/utils/cn';
import React, { useState, useCallback, useMemo } from 'react';
import { ListItemToggle } from '../molecules/ListItemToggle';
import { useDragAndDrop } from '@/shared/hooks';
import { reorder } from '@/shared/utils';
import { Icon } from '@/modules/Icon/Icon';
import { faGripDotsVertical } from '@fortawesome/pro-regular-svg-icons';

export interface ListWithToggleItem {
  id: string;
  title: string;
  description?: string;
  checked?: boolean;
  disabled?: boolean;
  draggable?: boolean;
}

export interface ListWithToggleProps {
  items: ListWithToggleItem[];
  onToggleChange?: (itemId: string, checked: boolean) => void;
  onItemsReorder?: (reorderedItems: ListWithToggleItem[]) => void;
  enableReordering?: boolean;
  className?: string;
}

export const ListWithToggle: React.FC<ListWithToggleProps> = ({
  items,
  onToggleChange,
  onItemsReorder,
  enableReordering = false,
  className,
}) => {
  const [localItems, setLocalItems] = useState(items);

  // Sincronizar items externos con el estado local
  React.useEffect(() => {
    setLocalItems(items);
  }, [items]);

  // Memoizar el handler de reorder
  const handleReorder = useCallback(
    (fromIndex: number, toIndex: number) => {
      setLocalItems((currentItems) => {
        const reorderedItems = reorder(currentItems, fromIndex, toIndex);
        // Llamar al callback en el próximo tick para no bloquear el render
        requestAnimationFrame(() => {
          onItemsReorder?.(reorderedItems);
        });
        return reorderedItems;
      });
    },
    [onItemsReorder]
  );

  const { handlers, draggedItemId, dragOverItemId } = useDragAndDrop({
    onReorder: handleReorder,
    liveReorder: true,
    throttleMs: 50, // Optimizar con 50ms de throttle
  });

  // Memoizar el icono para evitar re-crearlo
  const dragIcon = useMemo(
    () =>
      enableReordering ? <Icon name={faGripDotsVertical} size="sm" color="secondary" /> : undefined,
    [enableReordering]
  );

  return (
    <div
      className={cn(
        'ui-rounded-surface-card ui-border ui-border-default ui-bg-fill ui-p-8 ui-shadow-03',
        'ui-flex ui-min-w-[320px] ui-flex-col ui-gap-8',
        '[&>*]:ui-transition-transform [&>*]:ui-duration-200 [&>*]:ui-ease-out',
        className
      )}
    >
      {localItems.map((item, index) => {
        const isDragging = enableReordering && draggedItemId === item.id;
        const isDragOver = enableReordering && dragOverItemId === item.id;

        return (
          <ListItemToggle
            key={item.id}
            title={item.title}
            description={item.description}
            checked={item.checked}
            disabled={item.disabled}
            onChange={(checked) => onToggleChange?.(item.id, checked)}
            draggable={enableReordering && (item.draggable ?? true)}
            isDragging={isDragging}
            isDragOver={isDragOver}
            onDragStart={(e) => handlers.onDragStart(e, item.id, index)}
            onDragEnd={handlers.onDragEnd}
            onDragOver={(e) => handlers.onDragOver(e, item.id, index)}
            onDragLeave={handlers.onDragLeave}
            onDrop={(e) => handlers.onDrop(e, item.id, index)}
            dragHandleIcon={dragIcon}
          />
        );
      })}
    </div>
  );
};
